/**
 * TaskCreateFromEmailModal — portals into layout #taskModal CSR root.
 * Mirrors Hotwire tasks/new + _form.html.erb (in_modal: true) structure and CSS.
 * People / groups / tags use bootstrap5-tags against email/v1 autocomplete
 * (email-shared path builders — not Hotwire people_path / tags_tasks_path).
 */

import { h } from 'preact'
import { createPortal } from 'preact/compat'
import { useEffect, useRef, useState } from 'preact/hooks'
import Tags from 'bootstrap5-tags'
import {
  buildTaskTagsPath,
  buildPersonsPath,
  buildPersonGroupsPath,
} from '@tshmanager/email-shared'
import { emailApiBase, emailApiHeaders } from '../../modules/email/store/api.js'
import {
  openTaskModalHost,
  teardownTaskModalHost,
  getTaskModalEl,
} from './taskModalHost.js'
import { unmountTaskModalShell } from '../../modules/task_modal_shell.js'
import { RequiredAsterisk } from '../shared/RequiredAsterisk.jsx'

function selectedValues(selectEl) {
  if (!selectEl) return []
  return [...selectEl.selectedOptions].map(o => o.value).filter(Boolean)
}

function selectedNumbers(selectEl) {
  return selectedValues(selectEl).map(Number).filter(Boolean)
}

function tagsFetchOptions() {
  return {
    credentials: 'include',
    headers: emailApiHeaders(),
  }
}

function initTagsSelect(selectEl, options) {
  if (!selectEl?.id) return
  Tags.getInstance(selectEl)?.dispose()
  Tags.init(`#${selectEl.id}`, {
    queryParam: 'q',
    fetchOptions: tagsFetchOptions(),
    ...options,
  })
}

function disposeTagsSelect(selectEl) {
  if (!selectEl) return
  Tags.getInstance(selectEl)?.dispose()
}

export function TaskCreateFromEmailModal({ store, i18n }) {
  const open    = store.taskCreateOpen.value
  const emailId = store.taskCreateEmailId.value

  const hostRef     = useRef(null)
  const nameRef     = useRef(null)
  const tagsRef     = useRef(null)
  const peopleRef   = useRef(null)
  const groupsRef   = useRef(null)
  const [frameEl, setFrameEl] = useState(null)

  const [form, setForm]       = useState(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving]   = useState(false)
  const [error, setError]     = useState('')
  const [values, setValues]   = useState({})

  const t = (key, fallback) => {
    const msg = i18n?.t(key, { defaultValue: fallback })
    return !msg || /^\[missing /.test(msg) ? fallback : msg
  }

  const newTaskTitle = (() => {
    const model = t('activerecord.models.task.one', 'feladat').toLowerCase()
    const msg = i18n?.t('helpers.new', { model, defaultValue: `Új ${model} létrehozása` })
    return !msg || /^\[missing /.test(msg) ? `Új ${model} létrehozása` : msg
  })()

  const submitLabel = (() => {
    const msg = i18n?.t('helpers.submit.create', { model: t('activerecord.models.task.one', 'Feladat') })
    return !msg || /^\[missing /.test(msg) ? t('helpers.submit.create', 'Feladat létrehozása') : msg
  })()

  function setValue(key, value) {
    setValues(prev => ({ ...prev, [key]: value }))
  }

  useEffect(() => {
    const modal = getTaskModalEl()
    if (!modal) return undefined

    const onHidden = (event) => {
      if (event.target !== modal) return
      if (!hostRef.current) return
      teardownTaskModalHost(hostRef.current)
      hostRef.current = null
      setFrameEl(null)
      store.closeTaskCreateModal()
    }
    modal.addEventListener('hidden.bs.modal', onHidden)
    return () => modal.removeEventListener('hidden.bs.modal', onHidden)
  }, [])

  useEffect(() => {
    if (!open || !emailId) {
      if (hostRef.current) {
        const host = hostRef.current
        hostRef.current = null
        setFrameEl(null)
        if (host.shell) unmountTaskModalShell(host.shell)
        // Sibling modal may already be showing #taskModal — don't hide then.
        if (!store.taskLinkPickerOpen.value) host.bs?.hide()
      }
      return
    }

    const host = openTaskModalHost({ flexShell: true, extraClass: 'task-modal--task-form' })
    if (!host) return
    hostRef.current = host
    setFrameEl(host.root)

    setError('')
    setSaving(false)
    setLoading(true)
    setForm(null)

    let cancelled = false
    ;(async () => {
      try {
        const data = await store.fetchTaskForm(emailId)
        if (cancelled) return
        const defaults = data.defaults || {}
        setForm(data)
        setValues({
          name:                data.name || '',
          description:         '',
          building_complex_id: defaults.building_complex_id ?? '',
          status:              defaults.status ?? 'open',
          type_id:             defaults.type_id ?? '',
          responsible_id:      data.responsible_id ?? '',
          costbearer:          defaults.costbearer ?? '',
          priority:            defaults.priority ?? 1,
          duedate:             defaults.duedate ?? '',
          show_in_calendar:    !!defaults.show_in_calendar,
        })
        requestAnimationFrame(() => nameRef.current?.focus())
      } catch (err) {
        if (!cancelled) setError(t('tasks.create_failure', 'A feladat betöltése nem sikerült.'))
        console.error('[TaskCreateFromEmailModal] form load failed', err)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()

    return () => { cancelled = true }
  }, [open, emailId])

  // bootstrap5-tags after the form fields are in the DOM — email/v1 autocomplete.
  useEffect(() => {
    if (!form || loading) return undefined

    const tagsEl = tagsRef.current
    const peopleEl = peopleRef.current
    const groupsEl = groupsRef.current

    initTagsSelect(tagsEl, {
      allowClear: true,
      allowNew: true,
      liveServer: true,
      server: buildTaskTagsPath(emailApiBase()),
      serverDataKey: 'task_tags',
      labelField: 'tag',
      valueField: 'tag',
      searchFields: ['tag'],
      placeholder: t('emails.tag_search_placeholder', 'Címke keresése…'),
    })
    initTagsSelect(peopleEl, {
      allowClear: true,
      liveServer: true,
      // Base path only — bootstrap5-tags appends `?q=` (and would break a prebuilt query string).
      server: buildPersonsPath(emailApiBase()),
      serverParams: { purpose: 'task_affected' },
      serverDataKey: 'persons',
      labelField: 'name',
      valueField: 'id',
      searchFields: ['name'],
    })
    initTagsSelect(groupsEl, {
      allowClear: true,
      liveServer: true,
      server: buildPersonGroupsPath(emailApiBase()),
      serverDataKey: 'person_groups',
      labelField: 'name',
      valueField: 'id',
      searchFields: ['name'],
    })

    return () => {
      disposeTagsSelect(tagsEl)
      disposeTagsSelect(peopleEl)
      disposeTagsSelect(groupsEl)
    }
  }, [form, loading])

  async function handleSubmit(e) {
    e.preventDefault()
    if (saving || !emailId) return

    if (!values.name?.trim()) {
      setError(t('tasks.create_failure', 'A feladat létrehozása nem sikerült.'))
      nameRef.current?.focus()
      return
    }

    setSaving(true)
    setError('')

    const task = {
      name:             values.name.trim(),
      description:      values.description || '',
      status:           values.status,
      type_id:          values.type_id || null,
      responsible_id:   values.responsible_id || null,
      costbearer:       values.costbearer || null,
      priority:         values.priority,
      duedate:          values.duedate || null,
      show_in_calendar: values.show_in_calendar,
      tags:             selectedValues(tagsRef.current),
      people_ids:       selectedNumbers(peopleRef.current),
      person_group_ids: selectedNumbers(groupsRef.current),
    }
    if (values.building_complex_id) task.building_complex_id = values.building_complex_id

    try {
      await store.createTaskFromEmail(emailId, task)
      hostRef.current?.bs?.hide()
      store.closeTaskCreateModal()
    } catch (err) {
      setSaving(false)
      setError(err?.message || t('tasks.create_failure', 'A feladat létrehozása nem sikerült.'))
      console.error('[TaskCreateFromEmailModal] create failed', err)
    }
  }

  if (!open || !frameEl) return null

  const options = form?.options || {}
  const entitled = !!form?.task_automation_entitled

  const content = (
    <div class="task-form-modal d-flex flex-column flex-grow-1 min-h-0 w-100" data-turbo="false">
      <div class="modal-header border-bottom flex-shrink-0">
        <h5 class="modal-title d-flex align-items-center gap-2">
          <i class="bi bi-list-task" />
          {newTaskTitle}
        </h5>
        <button
          type="button"
          class="btn-close"
          data-bs-dismiss="modal"
          aria-label={t('helpers.close', 'Bezárás')}
        />
      </div>

      <form class="g-3 needs-validation task-form-modal-form d-flex flex-column flex-grow-1 min-h-0" onSubmit={handleSubmit}>
        <div class="modal-body">
          {error && <div class="alert alert-danger py-2 small">{error}</div>}

          {loading && (
            <div class="text-center py-5">
              <span class="spinner-border" role="status" />
            </div>
          )}

          {form && !loading && (
            <>
              <div class="row">
                <div class="col-12 col-lg-8">
                  <div class="card shadow-sm mb-4">
                    <div class="card-header bg-light d-flex align-items-center justify-content-between">
                      <h5 class="card-title mb-0 fw-bold">
                        <i class="bi bi-list-task text-primary me-2" />
                        {t('tasks.basic_information', 'Alapadatok')}
                      </h5>
                    </div>
                    <div class="card-body">
                      <div class="row mb-3">
                        <div class="col-12">
                          <i class="bi bi-tag me-1" />
                          <label class="form-label" for="email-task-name">
                            {t('activerecord.attributes.task.name', 'Név')}
                          </label>
                          <RequiredAsterisk />
                          <input
                            id="email-task-name"
                            ref={nameRef}
                            type="text"
                            class="form-control"
                            value={values.name}
                            onInput={e => setValue('name', e.target.value)}
                            required
                          />
                        </div>
                      </div>

                      <div class="row mb-3">
                        <div class="col-12">
                          <i class="bi bi-file-text me-1" />
                          <label class="form-label" for="email-task-description">
                            {t('activerecord.attributes.task.description', 'Leírás')}
                          </label>
                          <textarea
                            id="email-task-description"
                            class="form-control"
                            style="height: 300px"
                            value={values.description}
                            onInput={e => setValue('description', e.target.value)}
                          />
                        </div>
                      </div>

                      <div class="row mb-3">
                        <div class="col-md-6">
                          <i class="bi bi-building me-1" />
                          <label class="form-label" for="email-task-bc">
                            {t('activerecord.models.building_complex.one', 'Társasház')}
                          </label>
                          <select
                            id="email-task-bc"
                            class="form-select"
                            value={values.building_complex_id}
                            onChange={e => setValue('building_complex_id', e.target.value)}
                          >
                            {(options.building_complexes || []).map(bc => (
                              <option key={bc.id} value={bc.id}>{bc.name}</option>
                            ))}
                          </select>
                        </div>
                        <div class="col-md-6">
                          <i class="bi bi-tags me-1" />
                          <label class="form-label" for="email-task-tags">
                            {t('activerecord.attributes.task.tag_ids', 'Címkék')}
                          </label>
                          <select
                            id="email-task-tags"
                            ref={tagsRef}
                            class="form-select"
                            multiple
                          />
                        </div>
                      </div>

                      <div class="row mb-3">
                        <div class="col-md-6">
                          <i class="bi bi-people me-1" />
                          <label class="form-label" for="email-task-people">
                            {t('tasks.affected_people', 'Érintett személyek')}
                          </label>
                          <select
                            id="email-task-people"
                            ref={peopleRef}
                            class="form-select"
                            multiple
                          />
                        </div>
                        <div class="col-md-6">
                          <i class="bi bi-people-fill me-1" />
                          <label class="form-label" for="email-task-groups">
                            {t('tasks.affected_person_groups', t('tasks.person_groups', 'Személycsoportok'))}
                          </label>
                          <select
                            id="email-task-groups"
                            ref={groupsRef}
                            class="form-select"
                            multiple
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="col-12 col-lg-4">
                  <div class="card shadow-sm mb-4">
                    <div class="card-header bg-light">
                      <h5 class="card-title mb-0 fw-bold">
                        <i class="bi bi-gear text-secondary me-2" />
                        {t('tasks.properties', 'Tulajdonságok')}
                      </h5>
                    </div>
                    <div class="card-body">
                      <div class="row mb-3">
                        <div class="col-12">
                          <i class="bi bi-flag me-1" />
                          <label class="form-label" for="email-task-status">
                            {t('activerecord.attributes.task.status', 'Státusz')}
                          </label>
                          <select
                            id="email-task-status"
                            class="form-select"
                            value={values.status}
                            onChange={e => setValue('status', e.target.value)}
                          >
                            {(options.statuses || []).map(s => (
                              <option key={s.value} value={s.value}>{s.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div class="row mb-3">
                        <div class="col-12">
                          <i class="bi bi-tag me-1" />
                          <label class="form-label" for="email-task-type">
                            {t('activerecord.attributes.task.type_id', 'Típus')}
                            <RequiredAsterisk />
                          </label>
                          <select
                            id="email-task-type"
                            class="form-select"
                            value={values.type_id}
                            onChange={e => setValue('type_id', e.target.value)}
                            required
                          >
                            {(options.task_types || []).map(type => (
                              <option key={type.id} value={type.id}>{type.name}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div class="row mb-3">
                        <div class="col-12">
                          <i class="bi bi-person-check me-1" />
                          <label class="form-label" for="email-task-responsible">
                            {t('activerecord.attributes.task.responsible_id', 'Felelős')}
                            <RequiredAsterisk />
                          </label>
                          <select
                            id="email-task-responsible"
                            class="form-select"
                            value={values.responsible_id}
                            onChange={e => setValue('responsible_id', e.target.value)}
                            required
                          >
                            {(options.responsibles || []).map(p => (
                              <option key={p.id} value={p.id}>{p.name}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div class="row mb-3">
                        <div class="col-12">
                          <i class="bi bi-currency-dollar me-1" />
                          <label class="form-label" for="email-task-costbearer">
                            {t('activerecord.attributes.task.costbearer', 'Költségviselő')}
                          </label>
                          <select
                            id="email-task-costbearer"
                            class="form-select"
                            value={values.costbearer}
                            onChange={e => setValue('costbearer', e.target.value)}
                          >
                            {(options.costbearers || []).map(c => (
                              <option key={c.value} value={c.value}>{c.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div class="row mb-3">
                        <div class="col-12">
                          <i class="bi bi-calendar-event me-1" />
                          <label class="form-label" for="email-task-duedate">
                            {t('activerecord.attributes.task.duedate', 'Határidő')}
                          </label>
                          <RequiredAsterisk />
                          <input
                            id="email-task-duedate"
                            type="date"
                            class="form-control"
                            value={values.duedate || ''}
                            onInput={e => setValue('duedate', e.target.value)}
                            required
                          />
                        </div>
                      </div>

                      <div class="row mb-3">
                        <div class="col-12">
                          <i class="bi bi-exclamation-triangle me-1" />
                          <label class="form-label" for="email-task-priority">
                            {t('activerecord.attributes.task.priority', 'Prioritás')}
                          </label>
                          <select
                            id="email-task-priority"
                            class="form-select"
                            value={values.priority}
                            onChange={e => setValue('priority', Number(e.target.value))}
                          >
                            {(options.priorities || []).map(p => (
                              <option key={p.value} value={p.value}>{p.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div class="row mb-3">
                        <div class="col-12">
                          <div class="form-check">
                            <i class="bi bi-calendar3 me-1" />
                            <input
                              id="email-task-calendar"
                              class="form-check-input"
                              type="checkbox"
                              checked={values.show_in_calendar}
                              onChange={e => setValue('show_in_calendar', e.target.checked)}
                            />
                            <label class="form-check-label" for="email-task-calendar">
                              {t('tasks.calendar.show_in_calendar', 'Megjelenítés a naptárban')}
                            </label>
                            <div class="form-text">
                              {t('tasks.calendar.show_in_calendar_hint', '')}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="card shadow-sm mb-4">
                <div class="card-header bg-light d-flex justify-content-between align-items-center">
                  <h5 class="card-title mb-0 fw-bold">
                    <i class="bi bi-calendar3 text-info me-2" />
                    {t('tasks.scheduling', 'Ütemezés')}
                    {!entitled && <i class="bi bi-lock-fill text-muted ms-2" title={t('billing.locked', 'Zárolva')} />}
                  </h5>
                  <button
                    type="button"
                    class={`btn btn-sm ${entitled ? 'btn-outline-primary' : 'btn-outline-secondary'}`}
                    disabled
                    title={entitled
                      ? t('tasks.schedule.add_schedule', 'Ütemezés hozzáadása')
                      : t('billing.feature_locked', 'Nincs jogosultság')}
                  >
                    <i class={`bi ${entitled ? 'bi-plus-circle' : 'bi-lock-fill'} me-1`} />
                    {t('tasks.schedule.add_schedule', 'Ütemezés hozzáadása')}
                  </button>
                </div>
                <div class="card-body">
                  {!entitled && (
                    <div class="text-muted small">
                      {t('billing.task_automation_locked', 'Az automatikus ütemezés a jelenlegi csomagban nem érhető el.')}
                    </div>
                  )}
                  {entitled && (
                    <div class="text-muted small">
                      {t('tasks.schedule.empty_hint', 'Ütemezés a teljes feladat űrlapon adható hozzá.')}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        <div class="modal-footer justify-content-center flex-wrap gap-2">
          <button type="submit" class="btn btn-primary" disabled={saving || loading || !form}>
            {saving && <span class="spinner-border spinner-border-sm me-1" role="status" />}
            {submitLabel}
          </button>
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" disabled={saving}>
            <i class="bi bi-x-circle me-1" />
            {t('helpers.cancel', 'Mégse')}
          </button>
        </div>
      </form>
    </div>
  )

  return createPortal(content, frameEl)
}
